import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiChevronDown, FiCheck } from 'react-icons/fi';

interface EditInventoryItemModalProps {
  isOpen: boolean;
  editingInventoryItem: any;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  editItemName: string;
  setEditItemName: (val: string) => void;
  editItemCategory: string;
  setEditItemCategory: (val: string) => void;
  isEditCategoryDropdownOpen: boolean;
  setIsEditCategoryDropdownOpen: (val: boolean) => void;
  editItemAssetTag: string;
  setEditItemAssetTag: (val: string) => void;
  editItemQuantity: string;
  setEditItemQuantity: (val: string) => void;
  editItemBudgetAllocation: string;
  setEditItemBudgetAllocation: (val: string) => void;
  editItemLocation: string;
  setEditItemLocation: (val: string) => void;
  editItemCondition: 'Excellent' | 'Good' | 'Fair' | 'Needs Repair';
  setEditItemCondition: (val: 'Excellent' | 'Good' | 'Fair' | 'Needs Repair') => void;
  isSubmitting?: boolean;
  error?: string;
}

export const EditInventoryItemModal: React.FC<EditInventoryItemModalProps> = ({
  isOpen,
  editingInventoryItem,
  onClose,
  onSubmit,
  editItemName,
  setEditItemName,
  editItemCategory,
  setEditItemCategory,
  isEditCategoryDropdownOpen,
  setIsEditCategoryDropdownOpen,
  editItemAssetTag,
  setEditItemAssetTag,
  editItemQuantity,
  setEditItemQuantity,
  editItemBudgetAllocation,
  setEditItemBudgetAllocation,
  editItemLocation,
  setEditItemLocation,
  editItemCondition,
  setEditItemCondition,
  isSubmitting,
  error,
}) => {
  return (
    <AnimatePresence>
      {isOpen && editingInventoryItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/80 backdrop-blur-md"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative w-full max-w-lg bg-[#0F0F0F] border border-white/10 rounded-[32px] overflow-hidden z-10 shadow-[0_20px_50px_rgba(0,0,0,0.8)]"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/5">
              <div>
                <h3 className="text-base font-bold text-white">Edit Inventory Item</h3>
                <p className="text-zinc-500 text-xs mt-0.5">
                  Update inventory details and asset information.
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-full hover:bg-white/5 text-zinc-400 hover:text-white cursor-pointer transition-colors"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={onSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-zinc-400 text-xs font-semibold mb-1.5">
                  Item Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. Main PA System (JBL SRX)"
                  value={editItemName}
                  onChange={(e) => setEditItemName(e.target.value)}
                  required
                  className="w-full bg-[#141414] border border-white/10 outline-none text-white text-xs rounded-xl px-4 py-3 focus:border-blue-500 transition-all placeholder:text-zinc-600"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-zinc-400 text-xs font-semibold mb-1.5">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setIsEditCategoryDropdownOpen(!isEditCategoryDropdownOpen)}
                      className="w-full bg-[#141414] border border-white/10 hover:border-white/20 outline-none text-xs rounded-xl px-4 py-3 cursor-pointer flex items-center justify-between transition-all"
                    >
                      <span
                        className={editItemCategory ? 'text-white font-medium' : 'text-zinc-550'}
                      >
                        {editItemCategory || 'Select'}
                      </span>
                      <FiChevronDown
                        className={`w-4 h-4 text-zinc-400 transition-transform duration-200 ${
                          isEditCategoryDropdownOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </button>

                    <AnimatePresence>
                      {isEditCategoryDropdownOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: -6, scale: 0.98 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -6, scale: 0.98 }}
                          transition={{ duration: 0.15 }}
                          className="absolute top-full left-0 right-0 mt-1.5 z-50 bg-[#141414] border border-white/15 rounded-2xl p-1.5 shadow-[0_10px_35px_rgba(0,0,0,0.9)] max-h-52 overflow-y-auto no-scrollbar"
                        >
                          {[
                            'Audio & Sound',
                            'Lighting',
                            'Video & Visuals',
                            'Staging & Structures',
                            'Power & Electrical',
                            'Furniture & Seating',
                            'Décor & Branding',
                            'Catering & Bar',
                            'Music & DJ Equipment',
                            'Production & Event Operations',
                            'IT, Communication & Check-in',
                            'Safety, Security & Logistics',
                            'Miscellaneous',
                          ].map((cat) => {
                            const isSelected = editItemCategory === cat;
                            return (
                              <button
                                key={cat}
                                type="button"
                                onClick={() => {
                                  setEditItemCategory(cat);
                                  setIsEditCategoryDropdownOpen(false);
                                }}
                                className={`w-full text-left px-3.5 py-2.5 rounded-xl text-xs transition-all flex items-center justify-between cursor-pointer ${
                                  isSelected
                                    ? 'bg-[#0B2545] text-[#60A5FA] font-semibold'
                                    : 'text-zinc-300 hover:bg-white/5 hover:text-white'
                                }`}
                              >
                                <span>{cat}</span>
                                {isSelected && <FiCheck className="w-3.5 h-3.5 text-[#60A5FA]" />}
                              </button>
                            );
                          })}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
                <div>
                  <label className="block text-zinc-400 text-xs font-semibold mb-1.5">
                    Label / Asset Tag <span className="text-zinc-600 font-normal">(Optional)</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. AUD-001"
                    value={editItemAssetTag}
                    onChange={(e) => setEditItemAssetTag(e.target.value)}
                    className="w-full bg-[#141414] border border-white/10 outline-none text-white text-xs rounded-xl px-4 py-3 focus:border-blue-500 transition-all placeholder:text-zinc-600"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-zinc-400 text-xs font-semibold mb-1.5">
                    Quantity <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={editItemQuantity}
                    onChange={(e) => setEditItemQuantity(e.target.value)}
                    required
                    className="w-full bg-[#141414] border border-white/10 outline-none text-white text-xs rounded-xl px-4 py-3 focus:border-blue-500 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-zinc-400 text-xs font-semibold mb-1.5">
                    Budget Allocation <span className="text-zinc-600 font-normal">(Optional)</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={editItemBudgetAllocation}
                    onChange={(e) => setEditItemBudgetAllocation(e.target.value)}
                    className="w-full bg-[#141414] border border-white/10 outline-none text-white text-xs rounded-xl px-4 py-3 focus:border-blue-500 transition-all placeholder:text-zinc-600"
                  />
                </div>
              </div>

              <div>
                <label className="block text-zinc-400 text-xs font-semibold mb-1.5">
                  Location <span className="text-zinc-600 font-normal">(Optional)</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. Main Stage, VIP Area, Backstage"
                  value={editItemLocation}
                  onChange={(e) => setEditItemLocation(e.target.value)}
                  className="w-full bg-[#141414] border border-white/10 outline-none text-white text-xs rounded-xl px-4 py-3 focus:border-blue-500 transition-all placeholder:text-zinc-600"
                />
              </div>

              <div>
                <label className="block text-zinc-400 text-[10px] font-bold uppercase tracking-wider mb-2">
                  CONDITION <span className="text-red-500 font-semibold">*</span>
                </label>
                <div className="flex flex-wrap items-center gap-2">
                  {[
                    {
                      key: 'Excellent',
                      label: 'Excellent',
                      activeBg: 'bg-[#062c19] text-[#22c55e] border-[#10b981]',
                    },
                    {
                      key: 'Good',
                      label: 'Good',
                      activeBg: 'bg-[#0a1e38] text-[#3b82f6] border-[#1d4ed8]/30',
                    },
                    {
                      key: 'Fair',
                      label: 'Fair',
                      activeBg: 'bg-[#241c03] text-[#fbbf24] border-[#d97706]/30',
                    },
                    {
                      key: 'Needs Repair',
                      label: 'Needs Repair',
                      activeBg: 'bg-[#261013] text-[#f87171] border-[#dc2626]/30',
                    },
                  ].map((cond) => {
                    const isSelected = editItemCondition === cond.key;
                    return (
                      <button
                        key={cond.key}
                        type="button"
                        onClick={() => setEditItemCondition(cond.key as any)}
                        className={`px-4 py-1.5 rounded-full border text-xs font-medium transition-all cursor-pointer ${
                          isSelected
                            ? cond.activeBg
                            : 'bg-[#141414] border-white/10 text-zinc-400 hover:text-white hover:border-white/20'
                        }`}
                      >
                        {cond.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-xs p-3 rounded-xl">
                  {error}
                </div>
              )}

              <div className="flex items-center justify-end pt-4 border-t border-white/5">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-2.5 bg-white text-black hover:bg-zinc-200 disabled:opacity-50 rounded-full text-xs font-bold transition-all cursor-pointer active:scale-95 shadow-md flex items-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <span className="w-3 h-3 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <span>Save Changes</span>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
